import { useEffect, useState } from 'react'

/**
 * Which part of the day it is in Jakarta.
 *
 * Shared by the two screens that greet people -- login and Beranda -- so the
 * greeting and the colour of the sky are always the same on both, and change
 * at the same minute.
 */

export type TimeOfDay = 'pagi' | 'siang' | 'sore' | 'malam'

/** The hour in Jakarta, whatever the browser's own time zone says. */
export function jakartaHour(date: Date): number {
  return Number(
    new Intl.DateTimeFormat('id-ID', { hour: 'numeric', hour12: false, timeZone: 'Asia/Jakarta' }).format(date),
  )
}

/**
 * Four bands, the way people greet each other here. Before four in the
 * morning is still night: "Selamat pagi" at 02.00 reads as a bug.
 */
export function timeOfDay(hour: number): TimeOfDay {
  if (hour >= 4 && hour < 11) return 'pagi'
  if (hour >= 11 && hour < 15) return 'siang'
  if (hour >= 15 && hour < 19) return 'sore'
  return 'malam'
}

export const GREETING: Record<TimeOfDay, string> = {
  pagi: 'Selamat pagi',
  siang: 'Selamat siang',
  sore: 'Selamat sore',
  malam: 'Selamat malam',
}

/**
 * Now, re-read every half minute: enough for a clock that shows minutes and
 * for the sky to change at the hour, without re-rendering the page each second.
 */
export function useJakartaNow(): { now: Date; tod: TimeOfDay } {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(timer)
  }, [])
  return { now, tod: timeOfDay(jakartaHour(now)) }
}
